import { create } from "zustand";
import type {
  ActivityEvent,
  Agent,
  AtomicTask,
  CreditLedger,
  Goal,
  Integration,
  NotificationPreference,
  PendingAssignment,
  Project,
  RemoteAgentApproval,
  ReportsSnapshot,
  Subtask,
  Task,
  TaskOperation,
  TaskSnapshot,
  Workspace,
  WorkspaceMember
} from "@shared/domain";
import { makeId } from "@/utils/id";
import { calculateClarity } from "@/utils/tasks";
import type { ClarificationInput, CreateTaskInput, Toast, WizardFormState } from "@/state/types";
import { buildTaskPlanTemplate } from "@shared/taskPlanning";

interface AppState {
  workspace: Workspace;
  goals: Goal[];
  projects: Project[];
  tasks: Task[];
  subtasks: Subtask[];
  atomicTasks: AtomicTask[];
  agents: Agent[];
  discoverAgents: Agent[];
  members: WorkspaceMember[];
  pendingAssignments: PendingAssignment[];
  activities: ActivityEvent[];
  notifications: NotificationPreference[];
  integrations: Integration[];
  creditLedger: CreditLedger[];
  reports: ReportsSnapshot;
  archivedTaskIds: string[];
  taskOperations: Record<string, TaskOperation | undefined>;
  pendingAgentApprovals: Record<string, RemoteAgentApproval | undefined>;
  activeProjectId: string;
  activeTaskId?: string;
  selectedAgentId?: string;
  toasts: Toast[];
  theme: "light" | "dark" | "system";
  authUser: { id: string; name: string; email: string; platformRole?: "user" | "platform_admin" } | null;
  wizardForm: WizardFormState;
  isLoading: boolean;
  error?: string;
  setWorkspaceFromApi: (workspace: Workspace) => void;
  setGoalsFromApi: (goals: Goal[]) => void;
  setProjectsFromApi: (projects: Project[]) => void;
  setAgentsFromApi: (agents: Agent[]) => void;
  setDiscoverAgentsFromApi: (agents: Agent[]) => void;
  setReportsFromApi: (reports: ReportsSnapshot) => void;
  setMembersFromApi: (members: WorkspaceMember[]) => void;
  setPendingAssignmentsFromApi: (pendingAssignments: PendingAssignment[]) => void;
  setCreditLedgerFromApi: (creditLedger: CreditLedger[]) => void;
  setNotificationsFromApi: (notifications: NotificationPreference[]) => void;
  setIntegrationsFromApi: (integrations: Integration[]) => void;
  setTasksFromApi: (tasks: Task[]) => void;
  setSubtasksFromApi: (subtasks: Subtask[]) => void;
  setAtomicTasksFromApi: (atomicTasks: AtomicTask[]) => void;
  setActivitiesFromApi: (activities: ActivityEvent[]) => void;
  mergeTaskSnapshotFromApi: (snapshot: TaskSnapshot) => void;
  setTaskOperationForTask: (taskId: string, operation?: TaskOperation) => void;
  upsertTaskFromApi: (task: Task) => void;
  upsertProjectFromApi: (project: Project) => void;
  updateTaskLocal: (
    taskId: string,
    patch: Partial<Pick<Task, "title" | "description" | "dueAt" | "assignee" | "projectId"> & { definitionOfDone: string }>
  ) => Task | undefined;
  updateProjectLocal: (
    projectId: string,
    patch: Partial<
      Pick<
        Project,
        "title" | "description" | "goalIds" | "primaryGoalId" | "memberIds" | "defaultAgentId" | "color" | "icon" | "connectedToolIds" | "recommendedAgentIds" | "siteDispatchPolicy"
      >
    >
  ) => Project | undefined;
  setTheme: (theme: "light" | "dark" | "system") => void;
  setAuthUser: (user: { id: string; name: string; email: string; platformRole?: "user" | "platform_admin" } | null) => void;
  setActiveProject: (projectId: string) => void;
  setActiveTask: (taskId?: string) => void;
  setSelectedAgent: (agentId?: string) => void;
  updateWorkspace: (input: Pick<Workspace, "name" | "slug" | "industry" | "teamSize">) => void;
  updateWizardForm: (patch: Partial<WizardFormState>) => void;
  createTask: (input: CreateTaskInput) => Task;
  generateSubtasksForTask: (taskId: string) => void;
  submitClarification: (taskId: string, payload: ClarificationInput) => void;
  resolveBlocker: (taskId: string) => void;
  markAtomicDone: (atomicId: string) => void;
  toggleNotification: (id: string) => void;
  toggleIntegration: (id: string) => void;
  connectAgent: (agentId: string, connect: boolean) => void;
  hireAgent: (agentId: string) => void;
  inviteMember: (name: string, email: string, role: WorkspaceMember["role"]) => void;
  purchaseCredits: (credits: number, priceLabel: string) => void;
  archiveTask: (taskId: string) => void;
  restoreTask: (taskId: string) => void;
  pushToast: (toast: Omit<Toast, "id">) => void;
  dismissToast: (id: string) => void;
  hasExplicitThemePreference: boolean;
}

const nowIso = () => new Date().toISOString();
const AUTH_USER_STORAGE_KEY = "taskkar-auth-user";
export const THEME_STORAGE_KEY = "taskkar-theme-v2";
export const WORKSPACE_THEME_STORAGE_KEY = "taskkar-workspace-theme-v2";

const readPersistedTheme = (): { theme: "light" | "dark" | "system"; explicit: boolean } => {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (value === "light" || value === "dark" || value === "system") {
      return { theme: value, explicit: true };
    }
    const workspaceTheme = window.localStorage.getItem(WORKSPACE_THEME_STORAGE_KEY);
    if (workspaceTheme === "light" || workspaceTheme === "dark" || workspaceTheme === "system") {
      return { theme: workspaceTheme, explicit: false };
    }
  } catch {
    // no-op
  }
  return { theme: "system", explicit: false };
};

const readPersistedAuthUser = (): { id: string; name: string; email: string; platformRole?: "user" | "platform_admin" } | null => {
  try {
    const raw = window.localStorage.getItem(AUTH_USER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const body = parsed as Record<string, unknown>;
    if (typeof body.id !== "string" || typeof body.name !== "string" || typeof body.email !== "string") return null;
    const platformRole = body.platformRole === "platform_admin" ? "platform_admin" : body.platformRole === "user" ? "user" : undefined;
    return { id: body.id, name: body.name, email: body.email, platformRole };
  } catch {
    return null;
  }
};

const persistAuthUser = (authUser: { id: string; name: string; email: string; platformRole?: "user" | "platform_admin" } | null): void => {
  try {
    if (!authUser) {
      window.localStorage.removeItem(AUTH_USER_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(authUser));
  } catch {
    // no-op: storage is best effort only
  }
};

const addLedgerDebit = (state: AppState, amount: number, operationType: string, description: string, taskId?: string) => {
  const currentBalance = state.workspace.creditBalance;
  const newBalance = Number(Math.max(0, currentBalance - amount).toFixed(1));
  state.workspace = { ...state.workspace, creditBalance: newBalance };
  state.creditLedger = [
    {
      id: makeId("cr"),
      workspaceId: state.workspace.id,
      type: "debit",
      amount: -amount,
      operationType,
      taskId,
      description,
      balanceAfter: newBalance,
      createdAt: nowIso()
    },
    ...state.creditLedger
  ];

  if (newBalance <= 10) {
    state.toasts = [
      {
        id: makeId("toast"),
        kind: "warning",
        message: newBalance === 0 ? "Credits exhausted. New dispatches paused." : `Low balance: ${newBalance} credits remaining.`,
        persistent: newBalance === 0
      },
      ...state.toasts
    ];
  }
};

const normalizeProjectId = (projects: Project[], requestedId: string): string => {
  if (projects.some((project) => project.id === requestedId)) return requestedId;
  return projects[0]?.id ?? requestedId;
};

const generateSubtasks = (task: Task) => {
  const template = buildTaskPlanTemplate({
    title: task.title,
    description: task.description,
    assignee: task.assignee
  });

  const subtasks: Subtask[] = [];
  const atomic: AtomicTask[] = [];
  const subtaskIds = template.subtasks.map(() => makeId("sub"));

  let previousAtomicTailId: string | undefined;

  template.subtasks.forEach((subtaskTemplate, subtaskIndex) => {
    const subtaskId = subtaskIds[subtaskIndex];
    const atomicIds = subtaskTemplate.atomicTasks.map(() => makeId("atomic"));

    subtasks.push({
      id: subtaskId,
      taskId: task.id,
      title: subtaskTemplate.title,
      assignee: subtaskTemplate.assignee,
      status: subtaskTemplate.status,
      dependsOn: subtaskIndex === 0 ? [] : [subtaskIds[subtaskIndex - 1]],
      generatedByAI: true,
      atomicTaskIds: atomicIds
    });

    subtaskTemplate.atomicTasks.forEach((atomicTemplate, atomicIndex) => {
      const id = atomicIds[atomicIndex];
      const dependsOn: string[] = [];
      if (atomicIndex > 0) {
        dependsOn.push(atomicIds[atomicIndex - 1]);
      } else if (previousAtomicTailId) {
        dependsOn.push(previousAtomicTailId);
      }

      atomic.push({
        id,
        subtaskId,
        taskId: task.id,
        title: atomicTemplate.title,
        assignee: atomicTemplate.assignee,
        status: atomicTemplate.status,
        dependsOn,
        retryCount: 0,
        modelUsed: atomicTemplate.modelUsed,
        creditsCharged: atomicTemplate.creditsCharged,
        completedAt: atomicTemplate.completedAtNow || atomicTemplate.status === "done" ? nowIso() : undefined
      });
    });

    previousAtomicTailId = atomicIds.length > 0 ? atomicIds[atomicIds.length - 1] : previousAtomicTailId;
  });

  return { subtaskIds, subtasks, atomic };
};

const persistedTheme = readPersistedTheme();
const EMPTY_WORKSPACE: Workspace = {
  id: "",
  name: "",
  slug: "",
  industry: "General",
  teamSize: "small_team",
  creditBalance: 0,
  themePreference: "system"
};
const EMPTY_REPORTS: ReportsSnapshot = {
  tasksCompleted: 0,
  tasksCompletedToday: 0,
  aiVsHumanPct: 0,
  hoursSaved: 0,
  avgTaskCompletionHours: 0,
  agentUtilizationPct: 0,
  blockersResolved: 0,
  atomicTasksCompleted: { today: 0, week: 0, lifetime: 0 },
  velocity: [],
  goalTimeline: [],
  agentPerformance: [],
  creditConsumption: []
};

export const useAppStore = create<AppState>((set, get) => ({
  workspace: EMPTY_WORKSPACE,
  goals: [],
  projects: [],
  tasks: [],
  subtasks: [],
  atomicTasks: [],
  agents: [],
  discoverAgents: [],
  members: [],
  pendingAssignments: [],
  activities: [],
  notifications: [],
  integrations: [],
  creditLedger: [],
  reports: EMPTY_REPORTS,
  archivedTaskIds: [],
  taskOperations: {},
  pendingAgentApprovals: {},
  activeProjectId: "",
  activeTaskId: undefined,
  selectedAgentId: undefined,
  toasts: [],
  theme: persistedTheme.theme,
  hasExplicitThemePreference: persistedTheme.explicit,
  authUser: readPersistedAuthUser(),
  wizardForm: {
    goalTitle: "",
    targetDate: "",
    goalDescription: "",
    firstProject: "",
    inviteEmail: ""
  },
  isLoading: false,
  error: undefined,
  setWorkspaceFromApi: (workspace) =>
    set((state) => {
      if (!state.hasExplicitThemePreference) {
        try {
          window.localStorage.setItem(WORKSPACE_THEME_STORAGE_KEY, workspace.themePreference);
        } catch {
          // no-op
        }
      }
      return {
        ...state,
        workspace,
        theme: state.hasExplicitThemePreference ? state.theme : workspace.themePreference
      };
    }),
  setGoalsFromApi: (goals) => set((state) => ({ ...state, goals })),
  setProjectsFromApi: (projects) =>
    set((state) => ({
      ...state,
      projects,
      activeProjectId: projects.some((project) => project.id === state.activeProjectId) ? state.activeProjectId : (projects[0]?.id ?? state.activeProjectId)
    })),
  setAgentsFromApi: (agents) => set((state) => ({ ...state, agents })),
  setDiscoverAgentsFromApi: (discoverAgents) => set((state) => ({ ...state, discoverAgents })),
  setReportsFromApi: (reports) => set((state) => ({ ...state, reports })),
  setMembersFromApi: (members) => set((state) => ({ ...state, members })),
  setPendingAssignmentsFromApi: (pendingAssignments) => set((state) => ({ ...state, pendingAssignments })),
  setCreditLedgerFromApi: (creditLedger) => set((state) => ({ ...state, creditLedger })),
  setNotificationsFromApi: (notifications) => set((state) => ({ ...state, notifications })),
  setIntegrationsFromApi: (integrations) => set((state) => ({ ...state, integrations })),
  setTasksFromApi: (tasks) =>
    set((state) => ({
      ...state,
      tasks,
      archivedTaskIds: tasks.filter((task) => Boolean(task.archivedAt)).map((task) => task.id)
    })),
  setSubtasksFromApi: (subtasks) => set((state) => ({ ...state, subtasks })),
  setAtomicTasksFromApi: (atomicTasks) => set((state) => ({ ...state, atomicTasks })),
  setActivitiesFromApi: (activities) => set((state) => ({ ...state, activities })),
  mergeTaskSnapshotFromApi: (snapshot) =>
    set((state) => {
      const tasks = [...state.tasks];
      const taskIndex = tasks.findIndex((item) => item.id === snapshot.task.id);
      if (taskIndex >= 0) tasks[taskIndex] = snapshot.task;
      else tasks.unshift(snapshot.task);

      const otherSubtasks = state.subtasks.filter((item) => item.taskId !== snapshot.task.id);
      const otherAtomicTasks = state.atomicTasks.filter((item) => item.taskId !== snapshot.task.id);

      const activityById = new Map<string, ActivityEvent>();
      for (const event of state.activities) activityById.set(event.id, event);
      for (const event of snapshot.activity) activityById.set(event.id, event);
      const mergedActivity = Array.from(activityById.values()).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      return {
        ...state,
        tasks,
        subtasks: [...snapshot.subtasks, ...otherSubtasks],
        atomicTasks: [...snapshot.atomicTasks, ...otherAtomicTasks],
        activities: mergedActivity.slice(0, 400),
        taskOperations: {
          ...state.taskOperations,
          [snapshot.task.id]: snapshot.activeOperation
        },
        pendingAgentApprovals: {
          ...state.pendingAgentApprovals,
          [snapshot.task.id]: snapshot.pendingAgentApproval
        },
        archivedTaskIds: tasks.filter((task) => Boolean(task.archivedAt)).map((task) => task.id)
      };
    }),
  setTaskOperationForTask: (taskId, operation) =>
    set((state) => ({
      ...state,
      taskOperations: {
        ...state.taskOperations,
        [taskId]: operation
      }
    })),
  upsertTaskFromApi: (task) =>
    set((state) => {
      const current = state.tasks;
      const index = current.findIndex((item) => item.id === task.id);
      if (index < 0) {
        return { ...state, tasks: [task, ...current] };
      }
      const next = [...current];
      next[index] = task;
      return { ...state, tasks: next };
    }),
  upsertProjectFromApi: (project) =>
    set((state) => {
      const current = state.projects;
      const index = current.findIndex((item) => item.id === project.id);
      if (index < 0) return { ...state, projects: [project, ...current] };
      const next = [...current];
      next[index] = project;
      return { ...state, projects: next };
    }),
  updateTaskLocal: (taskId, patch) => {
    const current = get().tasks.find((task) => task.id === taskId);
    if (!current) return undefined;

    const extractSummaryAndDod = (description?: string): { summary?: string; definitionOfDone?: string } => {
      if (!description) return {};
      const lines = description
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
      const summaryLines: string[] = [];
      let definitionOfDone: string | undefined;
      for (const line of lines) {
        if (/^definition of done:/i.test(line)) {
          definitionOfDone = line.replace(/^definition of done:\s*/i, "").trim() || undefined;
          continue;
        }
        if (/^attachments:/i.test(line)) continue;
        summaryLines.push(line);
      }
      return {
        summary: summaryLines.join("\n") || undefined,
        definitionOfDone
      };
    };

    const parsed = extractSummaryAndDod(current.description);
    const summary = typeof patch.description === "string" ? patch.description.trim() : parsed.summary;
    const definitionOfDone = typeof patch.definitionOfDone === "string" ? patch.definitionOfDone.trim() : parsed.definitionOfDone;
    const descriptionSections = [summary, definitionOfDone ? `Definition of done: ${definitionOfDone}` : undefined];
    if (current.attachments.length > 0) descriptionSections.push(`Attachments: ${current.attachments.join(", ")}`);

    const merged: Task = {
      ...current,
      ...patch,
      description: patch.description !== undefined || patch.definitionOfDone !== undefined ? descriptionSections.filter(Boolean).join("\n\n") || undefined : current.description,
      updatedAt: nowIso()
    };

    set((state) => {
      const nextTasks = state.tasks.map((task) => (task.id === taskId ? merged : task));
      return { ...state, tasks: nextTasks };
    });
    return merged;
  },
  updateProjectLocal: (projectId, patch) => {
    const current = get().projects.find((project) => project.id === projectId);
    if (!current) return undefined;
    const merged: Project = {
      ...current,
      ...patch,
      updatedAt: nowIso()
    };
    set((state) => ({
      ...state,
      projects: state.projects.map((project) => (project.id === projectId ? merged : project))
    }));
    return merged;
  },
  setTheme: (theme) => {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // no-op
    }
    set({ theme, hasExplicitThemePreference: true });
  },
  setAuthUser: (authUser) => {
    persistAuthUser(authUser);
    set({ authUser });
  },
  setActiveProject: (activeProjectId) => set({ activeProjectId }),
  setActiveTask: (activeTaskId) => set({ activeTaskId }),
  setSelectedAgent: (selectedAgentId) => set({ selectedAgentId }),
  updateWorkspace: (input) => set((state) => ({ workspace: { ...state.workspace, ...input } })),
  updateWizardForm: (patch) => set((state) => ({ wizardForm: { ...state.wizardForm, ...patch } })),
  createTask: (input) => {
    const assignee: Task["assignee"] =
      input.assigneeMode === "agent"
        ? { type: "agent", agentId: input.assigneeId ?? "agent_code" }
        : input.assigneeMode === "member"
          ? { type: "member", userId: input.assigneeId ?? "user_2", role: "editor" }
          : { type: "auto", strategy: "skill_match" };

    const descriptionParts = [input.description?.trim(), input.definitionOfDone?.trim() ? `Definition of done: ${input.definitionOfDone.trim()}` : undefined].filter(
      (part): part is string => typeof part === "string" && part.length > 0
    );

    const resolvedProjectId = normalizeProjectId(get().projects, input.projectId);

    const created: Task = {
      id: makeId("task"),
      projectId: resolvedProjectId,
      workspaceId: get().workspace.id,
      title: input.title,
      description: descriptionParts.join("\n\n") || undefined,
      dueAt: input.dueAt,
      attachments: input.attachmentNames ?? [],
      assignee,
      autoApproveRemoteAgentActions: input.autoApproveRemoteAgentActions ?? false,
      clarityScore: calculateClarity(input.title, descriptionParts.join("\n\n")),
      status: "defining",
      subtaskIds: [],
      blockerKind: "none",
      createdAt: nowIso()
    };
    created.updatedAt = created.createdAt;

    set((state) => {
      const draft: AppState = { ...state };
      addLedgerDebit(draft, 1.0, "subtask_gen", "Subtask generation", created.id);

      if (created.clarityScore <= 0.75) {
        created.status = "blocked";
        created.blockedReason = "Low clarity score. Clarification required.";
        created.blockerKind = "clarity";
      } else {
        created.status = "in_progress";
        created.blockerKind = "none";
        const generated = generateSubtasks(created);
        created.subtaskIds = generated.subtaskIds;
        draft.subtasks = [...generated.subtasks, ...draft.subtasks];
        draft.atomicTasks = [...generated.atomic, ...draft.atomicTasks];
        addLedgerDebit(draft, 0.5, "atomic_dispatch", "Initial atomic dispatch", created.id);
      }

      draft.tasks = [created, ...draft.tasks];
      draft.activities = [
        {
          id: makeId("act"),
          workspaceId: draft.workspace.id,
          taskId: created.id,
          title: created.status === "blocked" ? `${created.title} — Needs clarification` : `${created.title} created`,
          detail: `${draft.projects.find((project) => project.id === created.projectId)?.title ?? "Project"} · Just now`,
          state: created.status === "blocked" ? "needs_input" : "queued",
          kind: created.status === "blocked" ? "blocker" : "info",
          projectName: draft.projects.find((project) => project.id === created.projectId)?.title ?? "Project",
          createdAt: nowIso()
        },
        ...draft.activities
      ];
      draft.toasts = [
        {
          id: makeId("toast"),
          kind: created.status === "blocked" ? "warning" : "success",
          message: created.status === "blocked" ? "Task created. Clarification needed to continue." : "Task created and execution started."
        },
        ...draft.toasts
      ];
      return draft;
    });

    return created;
  },
  generateSubtasksForTask: (taskId) => {
    set((state) => {
      const task = state.tasks.find((item) => item.id === taskId);
      if (!task) return state;
      if (task.subtaskIds.length > 0) return state;

      const generated = generateSubtasks(task);
      task.subtaskIds = generated.subtaskIds;
      task.status = "in_progress";
      task.blockerKind = "none";
      task.updatedAt = nowIso();

      return {
        ...state,
        tasks: [...state.tasks],
        subtasks: [...generated.subtasks, ...state.subtasks],
        atomicTasks: [...generated.atomic, ...state.atomicTasks]
      };
    });
  },
  submitClarification: (taskId, payload: ClarificationInput) => {
    set((state) => {
      const task = state.tasks.find((item) => item.id === taskId);
      if (!task) return state;
      const nextState: AppState = { ...state };
      addLedgerDebit(nextState, 0.1, "clarification", "Clarification generation", taskId);

      const clarityBoost = payload.context ? 0.2 : 0.15;
      task.clarityScore = Math.min(1, Number((task.clarityScore + clarityBoost).toFixed(2)));
      task.status = "in_progress";
      task.blockedReason = undefined;
      task.blockerKind = "none";
      task.updatedAt = nowIso();

      if (task.subtaskIds.length === 0) {
        const generated = generateSubtasks(task);
        task.subtaskIds = generated.subtaskIds;
        nextState.subtasks = [...generated.subtasks, ...nextState.subtasks];
        nextState.atomicTasks = [...generated.atomic, ...nextState.atomicTasks];
      }

      const clarificationEvent: ActivityEvent = {
        id: makeId("act"),
        workspaceId: nextState.workspace.id,
        taskId,
        title: `${task.title} clarification resolved`,
        detail: `${nextState.projects.find((project) => project.id === task.projectId)?.title ?? "Project"} · Just now`,
        state: "executed",
        kind: "completion",
        projectName: nextState.projects.find((project) => project.id === task.projectId)?.title ?? "Project",
        createdAt: nowIso()
      };
      nextState.activities = [clarificationEvent, ...nextState.activities];
      nextState.toasts = [
        {
          id: makeId("toast"),
          kind: "success",
          message: "Clarification captured. Plan generation resumed."
        },
        ...nextState.toasts
      ];

      return { ...nextState, tasks: [...nextState.tasks] };
    });
  },
  resolveBlocker: (taskId) => {
    set((state) => {
      const task = state.tasks.find((item) => item.id === taskId);
      if (!task) return state;
      task.status = "in_progress";
      task.blockedReason = undefined;
      task.blockerKind = "none";
      task.updatedAt = nowIso();
      const unblockEvent: ActivityEvent = {
        id: makeId("act"),
        workspaceId: state.workspace.id,
        taskId,
        title: `${task.title} unblocked`,
        detail: `${state.projects.find((project) => project.id === task.projectId)?.title ?? "Project"} · Just now`,
        state: "executed",
        kind: "completion",
        projectName: state.projects.find((project) => project.id === task.projectId)?.title ?? "Project",
        createdAt: nowIso()
      };
      const nextActivities: ActivityEvent[] = [unblockEvent, ...state.activities];
      return {
        ...state,
        tasks: [...state.tasks],
        activities: nextActivities,
        toasts: [{ id: makeId("toast"), kind: "success", message: "Blocker resolved. Execution resumed." }, ...state.toasts]
      };
    });
  },
  markAtomicDone: (atomicId) => {
    set((state) => {
      const atomic = state.atomicTasks.find((item) => item.id === atomicId);
      if (!atomic) return state;
      atomic.status = "done";
      atomic.completedAt = nowIso();

      const sameTask = state.atomicTasks.filter((item) => item.taskId === atomic.taskId && !item.archivedAt);
      const doneCount = sameTask.filter((item) => item.status === "done").length;
      const task = state.tasks.find((item) => item.id === atomic.taskId);

      if (task) {
        task.status = doneCount === sameTask.length ? "done" : "in_progress";
        task.blockerKind = "none";
        task.updatedAt = nowIso();
        if (task.status === "done") task.completedAt = nowIso();
      }

      state.reports = {
        ...state.reports,
        tasksCompletedToday: state.reports.tasksCompletedToday + (task?.status === "done" ? 1 : 0),
        hoursSaved: Number((state.reports.hoursSaved + 0.08).toFixed(1)),
        atomicTasksCompleted: {
          today: state.reports.atomicTasksCompleted.today + 1,
          week: state.reports.atomicTasksCompleted.week + 1,
          lifetime: state.reports.atomicTasksCompleted.lifetime + 1
        }
      };

      const nextActivities: ActivityEvent[] = [
        {
          id: makeId("act"),
          workspaceId: state.workspace.id,
          taskId: atomic.taskId,
          title: `${atomic.title} — Done`,
          detail: `${state.projects.find((project) => project.id === task?.projectId)?.title ?? "Project"} · Just now`,
          state: "executed",
          kind: "completion",
          projectName: state.projects.find((project) => project.id === task?.projectId)?.title ?? "Project",
          createdAt: nowIso()
        },
        ...state.activities
      ];

      return {
        ...state,
        tasks: [...state.tasks],
        atomicTasks: [...state.atomicTasks],
        activities: nextActivities,
        toasts: [
          {
            id: makeId("toast"),
            kind: task?.status === "done" ? "success" : "info",
            message: task?.status === "done" ? `Task complete: ${task.title}` : "Atomic step completed."
          },
          ...state.toasts
        ]
      };
    });
  },
  toggleNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.map((item) => (item.id === id ? { ...item, enabled: !item.enabled } : item))
    }));
  },
  toggleIntegration: (id) => {
    set((state) => ({
      integrations: state.integrations.map((item) =>
        item.id === id
          ? {
              ...item,
              status: item.status === "connected" ? "disconnected" : "connected",
              detail: item.status === "connected" ? undefined : "Connected"
            }
          : item
      ),
      toasts: [
        {
          id: makeId("toast"),
          kind: "info",
          message: "Integration state updated."
        },
        ...state.toasts
      ]
    }));
  },
  connectAgent: (agentId, connect) => {
    set((state) => {
      const agents = state.agents.map((agent) =>
        agent.id === agentId
          ? {
              ...agent,
              status: (connect ? "active" : "disconnected") as Agent["status"]
            }
          : agent
      );
      return {
        ...state,
        agents,
        toasts: [{ id: makeId("toast"), kind: "success", message: connect ? "Agent connected." : "Agent disconnected." }, ...state.toasts]
      };
    });
  },
  hireAgent: (agentId) => {
    set((state) => {
      const candidate = state.discoverAgents.find((agent) => agent.id === agentId);
      if (!candidate) return state;
      if (state.agents.some((agent) => agent.id === candidate.id)) return state;
      return {
        ...state,
        agents: [{ ...candidate, status: "active", usageThisMonth: 1, successRate: 1 }, ...state.agents],
        toasts: [{ id: makeId("toast"), kind: "success", message: `${candidate.name} added to your workspace.` }, ...state.toasts]
      };
    });
  },
  inviteMember: (name, email, role) => {
    set((state) => {
      const member: WorkspaceMember = {
        workspaceId: state.workspace.id,
        userId: makeId("user"),
        name,
        email,
        role,
        inviteStatus: "pending"
      };
      return {
        ...state,
        members: [member, ...state.members],
        pendingAssignments: [
          {
            id: makeId("pending"),
            taskId: "",
            inviteeEmail: email,
            inviteeName: name,
            workspaceId: state.workspace.id,
            invitedByUserId: state.authUser?.id ?? "user_1",
            expiresAt: new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString(),
            status: "pending",
            remindersSentCount: 0
          },
          ...state.pendingAssignments
        ],
        toasts: [{ id: makeId("toast"), kind: "success", message: "Invite sent." }, ...state.toasts]
      };
    });
  },
  purchaseCredits: (credits, priceLabel) => {
    set((state) => {
      const nextBalance = state.workspace.creditBalance + credits;
      return {
        ...state,
        workspace: { ...state.workspace, creditBalance: nextBalance },
        creditLedger: [
          {
            id: makeId("cr"),
            workspaceId: state.workspace.id,
            type: "purchase",
            amount: credits,
            description: `Purchased ${priceLabel}`,
            balanceAfter: nextBalance,
            createdAt: nowIso()
          },
          ...state.creditLedger
        ],
        toasts: [{ id: makeId("toast"), kind: "success", message: `Purchased ${credits} credits.` }, ...state.toasts]
      };
    });
  },
  archiveTask: (taskId) => {
    set((state) => {
      const task = state.tasks.find((item) => item.id === taskId);
      if (!task) return state;
      const archivedAt = nowIso();
      task.archivedExecutionState = JSON.stringify({
        status: task.status,
        blockedReason: task.blockedReason,
        blockerKind: task.blockerKind,
        completedAt: task.completedAt
      });
      task.archivedAt = archivedAt;
      task.status = "blocked";
      task.blockedReason = "Archived task";
      task.blockerKind = "system";
      task.updatedAt = archivedAt;
      for (const subtask of state.subtasks) {
        if (subtask.taskId === taskId && !subtask.archivedAt) {
          subtask.archivedAt = archivedAt;
        }
      }
      for (const atomicTask of state.atomicTasks) {
        if (atomicTask.taskId === taskId && !atomicTask.archivedAt) {
          atomicTask.archivedAt = archivedAt;
        }
      }
      return {
        ...state,
        tasks: [...state.tasks],
        subtasks: [...state.subtasks],
        atomicTasks: [...state.atomicTasks],
        archivedTaskIds: Array.from(new Set([taskId, ...state.archivedTaskIds])),
        toasts: [{ id: makeId("toast"), kind: "info", message: "Task archived." }, ...state.toasts]
      };
    });
  },
  restoreTask: (taskId) => {
    set((state) => {
      const task = state.tasks.find((item) => item.id === taskId);
      if (!task) return state;
      let restoredStatus: Task["status"] = "pending";
      let restoredReason: string | undefined;
      let restoredKind: Task["blockerKind"] = "none";
      let restoredCompletedAt: string | undefined;
      try {
        const restore = JSON.parse(task.archivedExecutionState ?? "{}") as Record<string, unknown>;
        const status = typeof restore.status === "string" ? restore.status : undefined;
        if (status === "pending" || status === "defining" || status === "in_progress" || status === "blocked" || status === "done") {
          restoredStatus = status;
        }
        restoredReason = typeof restore.blockedReason === "string" ? restore.blockedReason : undefined;
        const restoreKind = typeof restore.blockerKind === "string" ? restore.blockerKind : undefined;
        if (restoreKind === "clarity" || restoreKind === "dependency" || restoreKind === "manual_input" || restoreKind === "system" || restoreKind === "none") {
          restoredKind = restoreKind;
        }
        restoredCompletedAt = typeof restore.completedAt === "string" ? restore.completedAt : undefined;
      } catch {
        restoredStatus = task.blockedReason ? "blocked" : "pending";
      }

      task.archivedAt = undefined;
      task.status = restoredStatus;
      task.blockedReason = restoredReason;
      task.blockerKind = restoredKind;
      task.completedAt = restoredCompletedAt;
      task.updatedAt = nowIso();
      for (const subtask of state.subtasks) {
        if (subtask.taskId === taskId) {
          subtask.archivedAt = undefined;
        }
      }
      for (const atomicTask of state.atomicTasks) {
        if (atomicTask.taskId === taskId) {
          atomicTask.archivedAt = undefined;
        }
      }
      return {
        ...state,
        tasks: [...state.tasks],
        subtasks: [...state.subtasks],
        atomicTasks: [...state.atomicTasks],
        archivedTaskIds: state.archivedTaskIds.filter((id) => id !== taskId),
        toasts: [{ id: makeId("toast"), kind: "success", message: "Task restored." }, ...state.toasts]
      };
    });
  },
  pushToast: (toast) => set((state) => ({ toasts: [{ id: makeId("toast"), ...toast }, ...state.toasts] })),
  dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) }))
}));

export const selectActiveProject = (state: AppState) => state.projects.find((project) => project.id === state.activeProjectId) ?? state.projects[0];

export const selectVisibleTasks = (state: AppState) =>
  state.tasks.filter((task) => task.projectId === state.activeProjectId && !task.archivedAt);

export const selectBlockedTasks = (state: AppState) =>
  state.tasks.filter((task) => task.status === "blocked" && !task.archivedAt);

export const selectInProgressTasks = (state: AppState) =>
  state.tasks.filter((task) => task.status === "in_progress" && !task.archivedAt);

export const selectDoneTasks = (state: AppState) =>
  state.tasks.filter((task) => task.status === "done" && !task.archivedAt);
